/**
 * The default room plan for a Fixed Departure party that has no rooms yet
 * (pick-only create, old clients, itineraries saved before rooms existed).
 *
 * Shared by the API (prices the plan with the room engine) and the admin
 * config panel (seeds the room editor), so the stored price always matches the
 * stored rooms. The plan is always capacity-valid: every room has at least one
 * adult and at most `S + 1` bed-needing pax (adults + children with bed), where
 * `S` is the tour's sharing occupancy.
 *
 * Rule:
 * - S = sharingPaxCount > 0 ? sharingPaxCount : 2; A = max(1, adults).
 * - rooms = max(1, floor(A / S)), increased until A <= rooms * (S + 1).
 * - Base adults: each room in order takes min(S, remaining). Leftover adults
 *   go one per room (in order) as the extra-bed adult.
 * - Children with bed: each goes to the room with a free bed that has the
 *   fewest bed-needing pax (ties -> lowest index). No free bed -> the child
 *   becomes a child without bed.
 * - Children without bed: round-robin from room 1. Infants: all in room 1.
 */

import type { RoomGroupInput } from '../schemas/itineraries.js';

export interface FdDefaultRoomPlanParty {
  adults: number;
  childrenWithBed: number;
  childrenWithoutBed: number;
  infants: number;
}

const count = (n: number): number => (Number.isFinite(n) && n > 0 ? Math.floor(n) : 0);

export const defaultRoomPlanForParty = (
  party: FdDefaultRoomPlanParty,
  sharingPaxCount: number | null,
): RoomGroupInput[] => {
  const sharing = sharingPaxCount && sharingPaxCount > 0 ? Math.floor(sharingPaxCount) : 2;
  const adults = Math.max(1, count(party.adults));

  let roomCount = Math.max(1, Math.floor(adults / sharing));
  while (adults > roomCount * (sharing + 1)) roomCount += 1;

  const rooms = Array.from({ length: roomCount }, () => ({
    adults: 0,
    withBed: 0,
    withoutBed: 0,
    infants: 0,
  }));

  // Base adults, S per room in order.
  let remaining = adults;
  for (const room of rooms) {
    const take = Math.min(sharing, remaining);
    room.adults = take;
    remaining -= take;
  }
  // Leftover adults: one extra-bed adult per room, in order.
  for (let i = 0; remaining > 0 && i < rooms.length; i += 1) {
    rooms[i]!.adults += 1;
    remaining -= 1;
  }

  // Children with bed: the free-bed room with the fewest bed-needing pax.
  let overflowToNoBed = 0;
  for (let c = 0; c < count(party.childrenWithBed); c += 1) {
    let best = -1;
    let bestLoad = Number.POSITIVE_INFINITY;
    rooms.forEach((room, i) => {
      const load = room.adults + room.withBed;
      if (load < sharing + 1 && load < bestLoad) {
        best = i;
        bestLoad = load;
      }
    });
    if (best === -1) overflowToNoBed += 1;
    else rooms[best]!.withBed += 1;
  }

  // Children without bed (including with-bed overflow): round-robin.
  const withoutBed = count(party.childrenWithoutBed) + overflowToNoBed;
  for (let c = 0; c < withoutBed; c += 1) rooms[c % roomCount]!.withoutBed += 1;

  rooms[0]!.infants = count(party.infants);

  return [
    {
      label: null,
      rooms: rooms.map((r) => ({
        adults: r.adults,
        children: r.withBed + r.withoutBed,
        infants: r.infants,
        childrenNoBed: r.withoutBed,
      })),
    },
  ];
};
